import { useState } from 'react';
import { Alert } from '@utils/alert.ts';

interface User {
    id: string;
    name: string;
    email: string;
}

const PracticalExamples = () => {
    const [users, setUsers] = useState<User[]>([
        { id: '1', name: '홍길동', email: 'hong@example.com' },
        { id: '2', name: '김철수', email: 'kim@example.com' },
        { id: '3', name: '이영희', email: 'lee@example.com' },
    ]);

    // 사용자 추가
    const handleAddUser = async () => {
        const result = await Alert.multiInput('새 사용자 추가', {
            name: { placeholder: '이름', type: 'text' },
            email: { placeholder: '이메일', type: 'email' },
        });

        if (result.isConfirmed) {
            const { name, email } = result.value;

            if (!name || !email) {
                Alert.error('입력 오류', '모든 필드를 입력해주세요.');
                return;
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                Alert.error('입력 오류', '올바른 이메일 형식이 아닙니다.');
                return;
            }

            const newUser: User = {
                id: Date.now().toString(),
                name,
                email,
            };

            setUsers([...users, newUser]);
            Alert.success('추가 완료', '새 사용자가 추가되었습니다.');
        }
    };

    // 사용자 삭제
    const handleDeleteUser = async (user: User) => {
        const result = await Alert.delete(
            `${user.name}을(를) 삭제하시겠습니까?`,
            '이 작업은 되돌릴 수 없습니다.',
        );

        if (result.isConfirmed) {
            setUsers(users.filter(u => u.id !== user.id));
            Alert.toast('사용자가 삭제되었습니다', 'success');
        }
    };

    // 사용자 수정
    const handleEditUser = async (user: User) => {
        const result = await Alert.multiInput('사용자 정보 수정', {
            name: { placeholder: '이름', type: 'text' },
            email: { placeholder: '이메일', type: 'email' },
        });

        if (result.isConfirmed) {
            const { name, email } = result.value;

            if (!name || !email) {
                Alert.error('입력 오류', '모든 필드를 입력해주세요.');
                return;
            }

            setUsers(
                users.map(u => (u.id === user.id ? { ...u, name, email } : u)),
            );
            Alert.success('수정 완료', '사용자 정보가 수정되었습니다.');
        }
    };

    // 대량 삭제
    const handleBulkDelete = async () => {
        if (users.length === 0) {
            Alert.warning('삭제할 데이터 없음', '삭제할 사용자가 없습니다.');
            return;
        }

        const result = await Alert.confirm(
            `모든 사용자를 삭제하시겠습니까?`,
            `총 ${users.length}명의 사용자가 삭제됩니다.`,
            '모두 삭제',
            '취소',
        );

        if (result.isConfirmed) {
            Alert.loading('삭제 중...', '모든 사용자를 삭제하고 있습니다.');

            // 가짜 API 호출 시뮬레이션
            setTimeout(() => {
                setUsers([]);
                Alert.close();
                Alert.success('삭제 완료', '모든 사용자가 삭제되었습니다.');
            }, 2000);
        }
    };

    // 데이터 내보내기
    const handleExport = async () => {
        const result = await Alert.custom({
            title: '데이터 내보내기',
            text: '어떤 형식으로 내보내시겠습니까?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'JSON',
            cancelButtonText: '취소',
            showDenyButton: true,
            denyButtonText: 'CSV',
            denyButtonColor: '#28a745',
        });

        if (result.isConfirmed) {
            // JSON 형식으로 내보내기
            const jsonData = JSON.stringify(users, null, 2);
            const blob = new Blob([jsonData], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'users.json';
            a.click();
            URL.revokeObjectURL(url);
            Alert.success('내보내기 완료', 'JSON 파일이 다운로드되었습니다.');
        } else if (result.isDenied) {
            // CSV 형식으로 내보내기
            const csvData = [
                'ID,Name,Email',
                ...users.map(user => `${user.id},${user.name},${user.email}`),
            ].join('\n');
            const blob = new Blob([csvData], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'users.csv';
            a.click();
            URL.revokeObjectURL(url);
            Alert.success('내보내기 완료', 'CSV 파일이 다운로드되었습니다.');
        }
    };

    // 설정 저장
    // const handleSaveSettings = async () => {
    //     const steps = [
    //         {
    //             title: '알림 설정',
    //             text: '알림을 받으시겠습니까?',
    //             input: 'checkbox',
    //         },
    //         { title: '테마 설정', text: '테마를 선택하세요' },
    //         { title: '언어 설정', text: '언어를 선택하세요' },
    //     ];
    //
    //     try {
    //         const results = await Alert.queue(steps);
    //         Alert.success('설정 저장 완료', '모든 설정이 저장되었습니다.');
    //         console.log('설정 결과:', results);
    //     } catch (error) {
    //         Alert.info('설정 취소', '설정이 취소되었습니다.');
    //     }
    // };

    // 네트워크 요청 시뮬레이션
    // const handleNetworkRequest = async () => {
    //     Alert.loading(
    //         '데이터 로딩 중...',
    //         '서버에서 데이터를 가져오고 있습니다.',
    //     );
    //
    //     try {
    //         // 가짜 API 호출
    //         await new Promise((resolve, reject) => {
    //             setTimeout(() => {
    //                 // 50% 확률로 성공/실패
    //                 if (Math.random() > 0.5) {
    //                     resolve('success');
    //                 } else {
    //                     reject(new Error('네트워크 오류'));
    //                 }
    //             }, 3000);
    //         });
    //
    //         Alert.close();
    //         Alert.success('로딩 완료', '데이터를 성공적으로 불러왔습니다.');
    //     } catch (error) {
    //         Alert.close();
    //         Alert.error(
    //             '로딩 실패',
    //             '데이터를 불러오는 중 오류가 발생했습니다.',
    //         );
    //     }
    // };

    // 폼 유효성 검사
    const handleFormValidation = async () => {
        const result = await Alert.custom({
            title: '회원가입',
            html: `
        <form id="signup-form">
          <input id="username" class="swal2-input" placeholder="사용자명" required>
          <input id="email" class="swal2-input" placeholder="이메일" type="email" required>
          <input id="password" class="swal2-input" placeholder="비밀번호" type="password" required>
          <input id="confirmPassword" class="swal2-input" placeholder="비밀번호 확인" type="password" required>
        </form>
      `,
            confirmButtonText: '가입하기',
            showCancelButton: true,
            cancelButtonText: '취소',
            preConfirm: () => {
                const username = (
                    document.getElementById('username') as HTMLInputElement
                ).value;
                const email = (
                    document.getElementById('email') as HTMLInputElement
                ).value;
                const password = (
                    document.getElementById('password') as HTMLInputElement
                ).value;
                const confirmPassword = (
                    document.getElementById(
                        'confirmPassword',
                    ) as HTMLInputElement
                ).value;

                if (!username || !email || !password || !confirmPassword) {
                    Alert.showValidationMessage('모든 필드를 입력해주세요.');
                    return false;
                }

                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                    Alert.showValidationMessage(
                        '올바른 이메일 형식이 아닙니다.',
                    );
                    return false;
                }

                if (password.length < 6) {
                    Alert.showValidationMessage(
                        '비밀번호는 6자 이상이어야 합니다.',
                    );
                    return false;
                }

                if (password !== confirmPassword) {
                    Alert.showValidationMessage(
                        '비밀번호가 일치하지 않습니다.',
                    );
                    return false;
                }

                return { username, email, password };
            },
        });

        if (result.isConfirmed) {
            Alert.success('회원가입 완료', '성공적으로 가입되었습니다.');
        }
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2>실용적인 SweetAlert2 예시</h2>

            <div style={{ marginBottom: '20px' }}>
                <h3>사용자 관리</h3>
                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        marginBottom: '10px',
                    }}
                >
                    <button onClick={handleAddUser}>사용자 추가</button>
                    <button onClick={handleBulkDelete}>모두 삭제</button>
                    <button onClick={handleExport}>데이터 내보내기</button>
                </div>

                <div
                    style={{
                        border: '1px solid #ddd',
                        borderRadius: '5px',
                        padding: '10px',
                    }}
                >
                    {users.length === 0 ? (
                        <p>사용자가 없습니다.</p>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {users.map(user => (
                                <li
                                    key={user.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px',
                                        borderBottom: '1px solid #eee',
                                    }}
                                >
                                    <div>
                                        <strong>{user.name}</strong> -{' '}
                                        {user.email}
                                    </div>
                                    <div
                                        style={{ display: 'flex', gap: '5px' }}
                                    >
                                        <button
                                            onClick={() => handleEditUser(user)}
                                            style={{
                                                padding: '5px 10px',
                                                fontSize: '12px',
                                            }}
                                        >
                                            수정
                                        </button>
                                        <button
                                            onClick={() =>
                                                handleDeleteUser(user)
                                            }
                                            style={{
                                                padding: '5px 10px',
                                                fontSize: '12px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                            }}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <h3>기타 기능</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {/*<button onClick={handleSaveSettings}>설정 저장</button>*/}
                    {/*<button onClick={handleNetworkRequest}>*/}
                    {/*    네트워크 요청*/}
                    {/*</button>*/}
                    <button onClick={handleFormValidation}>
                        폼 유효성 검사
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PracticalExamples;
